import './App.css';
import { useEffect, useRef, useState } from 'react';
import { Hand } from 'pokersolver';
import useSound from '@mrmartineau/use-sound';
import { DndContext, TouchSensor, MouseSensor, useSensor, useSensors } from '@dnd-kit/core';
import { Droppable } from './Droppable';
import { Draggable } from './Draggable';
import { SettingsModal } from './modals/SettingsModal';
import { HelpModal } from './modals/HelpModal';
import { VictoryModal } from './modals/VictoryModal';
import { CollectionModal } from './modals/CollectionModal';
import { GiftClaimModal } from './modals/GiftClaimModal';
import { SecretDeckVaultModal } from './modals/SecretDeckVaultModal';
import { SharedCardVariantDefs } from './CardVariantOverlay';
import CardVariantOverlay from './CardVariantOverlay';
import { SUIT_SYMBOLS, randomizeCardVariant, getDisplayCard, getCardVariant, getBaseCard } from './utils/cards';
import { getSeasonalThemeClass } from './utils/seasonal';
import { KEYS, getDeck, consumeGiftCard, consumeGiftCardFromToken, parseGiftCardToken, getGiftCard } from './utils/storage';
import { humanCardName } from './utils/cards';

// sounds
import clickSfx from './sounds/small-click.mp3';
import tadaSfx from './sounds/tada.mp3';

function App() {

  // dnd setup
  const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor));

  // game logic
  const [init, setInit] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [isHardMode, setIsHardMode] = useState(false);
  const [isWinner, setIsWinner] = useState(false);
  const [victoryModalOpen, setVictoryModalOpen] = useState(false);
  const [earnedCard, setEarnedCard] = useState(null);
  const [helpModalOpen, setHelpModalOpen] = useState(false);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [winningsModalOpen, setWinningsModalOpen] = useState(false);
  const [hasOpenedTodaysBoosterPack, setHasOpenedTodaysBoosterPack] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [showVariantCards, setShowVariantCards] = useState(false);
  const [useClassicalCards, setUseClassicalCards] = useState(false);
  const [playerDeck, setPlayerDeck] = useState({});
  const [traySlots, setTraySlots] = useState([]);
  const [giftCardImportMessage, setGiftCardImportMessage] = useState(null);
  const [claimedGiftCard, setClaimedGiftCard] = useState(null);
  const [giftClaimModalOpen, setGiftClaimModalOpen] = useState(false);
  const [pendingGiftToken, setPendingGiftToken] = useState(() => new URLSearchParams(window.location.search).get('gift'));
  const [deckVaultModalOpen, setDeckVaultModalOpen] = useState(false);
  const [deckVaultFilterVariant, setDeckVaultFilterVariant] = useState(null);
  const [collectionRefreshTick, setCollectionRefreshTick] = useState(0);
  const [giftsEnabled] = useState(true);

  // puzzle logic
  const [puzzle, setPuzzle] = useState({
    square: new Array(25).fill('??')
  });
  const [concernedHands, setConcernedHands] = useState([]);
  const [playCards, setPlayCards] = useState([]);
  const [placedCards, setPlacedCards] = useState([]);

  // timings
  const [hrs, setHrs] = useState(0);
  const [mins, setMins] = useState(0);
  const [secs, setSecs] = useState(0);

  // misc
  const [deck] = useState(['Jh', '4d', 'Kc', '2h', '2c', 'Qh', '9d', 'Ah', '2d', 'Td', 'Jc', '3s', '3c', '3d', '8h', 'Ts', '2s', 'As', 'Th', 'Ks', 'Kd', '5h', '9c', 'Js', 'Kh', '7c', '6s', '4h', '3h', '5s', '7h', 'Qs', 'Qc', '9h', '8c', '9s', '5c', '4s', 'Jd', '8s', '8d', 'Qd', 'Ad', '7d', 'Tc', '6h', 'Ac', '5d', '7s', '6c', '6d', '4c'].sort(() => 0.5 - Math.random()).slice(0, 9));

  const intervalRef = useRef(null);

  // sound
  const [playClickSound, { unlock }] = useSound(clickSfx);
  const [playTadaSound] = useSound(tadaSfx);
  const cardImageFolder = useClassicalCards ? 'simoncards' : 'smallcards';

  // misc methods
  const splitIntoTenHands = (cards) => [
    cards.slice(0, 5), cards.slice(5, 10), cards.slice(10, 15), cards.slice(15, 20), cards.slice(20, 25),
    [cards[0], cards[5], cards[10], cards[15], cards[20]],
    [cards[1], cards[6], cards[11], cards[16], cards[21]],
    [cards[2], cards[7], cards[12], cards[17], cards[22]],
    [cards[3], cards[8], cards[13], cards[18], cards[23]],
    [cards[4], cards[9], cards[14], cards[19], cards[24]],
  ];

  const getShareData = () => {

    const formattedTime = () => {
      // Output like "1:01" or "4:03:59" or "123:03:59"
      let ret = '';
      if (hrs > 0) {
        ret += '' + hrs + ':' + (mins < 10 ? '0' : '');
      }
      ret += '' + mins + ':' + (secs < 10 ? '0' : '');
      ret += '' + secs;
      return ret;
    };

    const shareHeader = `Poqueta! ${puzzle.name} | ${formattedTime()} ${isHardMode ? '& psychic 🎱' : ''}`;
    const shareOrder = `Order: ${placedCards
      .map((pc) => pc.card)
      .join('')
      .replace(/[dchs]/g, (m) => SUIT_SYMBOLS[m])
      .toUpperCase()
      .replaceAll('OR', 'Jkr.')
      }`;
    const shareWinnings = isWinner && hasOpenedTodaysBoosterPack ? `Got the ${humanCardName(earnedCard)}!` : '';
    const shareFooter = window.location.hostname;

    const shareData = {
      text: `${shareHeader}\n${shareOrder}${shareWinnings.length > 0 ? '\n' + shareWinnings : ''}\n${shareFooter}`,
    };

    return shareData;

  };

  const handleShare = () => {
    navigator.share(getShareData()).catch((err) => {
      console.error('Something went wrong', err);
    });
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(getShareData().text);
    setIsCopied(true);
    setTimeout(() => {
      setIsCopied(false);
    }, 4000);
  };

  const handleBoosterPackOpened = () => {
    setHasOpenedTodaysBoosterPack(true);
    localStorage.setItem(KEYS.HAS_OPENED_TODAYS_BOOSTER_PACK, JSON.stringify(true));
  };

  useEffect(() => {

    if (!init || !gameStarted) return;

    // wow react sucks at interval stuff!
    // sorry for these hacks
    intervalRef.current = setInterval(() => {

      if (!isWinner) {
        const elapsed = ~~(localStorage.getItem(KEYS.TIME) || 0) + 1;
        localStorage.setItem(KEYS.TIME, elapsed);
      }

    }, 1000);

    return () => {
      clearInterval(intervalRef.current);
    };
  }, [isWinner, init, gameStarted]);

  const handleClaimGift = () => {
    if (!pendingGiftToken) return;

    unlock();
    setGameStarted(true);

    const giftTokenOrId = pendingGiftToken;
    const tokenPayload = parseGiftCardToken(giftTokenOrId);
    const legacyGift = tokenPayload ? null : getGiftCard(giftTokenOrId);

    // Try to consume the gift card
    const success = consumeGiftCardFromToken(giftTokenOrId) || consumeGiftCard(giftTokenOrId);

    if (success) {
      const claimedCard = tokenPayload?.card || legacyGift?.card || null;
      if (claimedCard) {
        setClaimedGiftCard(claimedCard);
        setGiftClaimModalOpen(true);
      } else {
        setGiftCardImportMessage({
          type: 'success',
          text: 'Gift card imported! Check your collection.'
        });
      }
    } else {
      setGiftCardImportMessage({
        type: 'error',
        text: 'Gift card expired or already used.'
      });
    }

    // Remove the gift parameter from URL
    const params = new URLSearchParams(window.location.search);
    params.delete('gift');
    const newUrl = params.toString() ? `${window.location.pathname}?${params.toString()}` : window.location.pathname;
    window.history.replaceState({}, document.title, newUrl);

    // Clear message after 5 seconds
    setTimeout(() => {
      setGiftCardImportMessage(null);
    }, 5000);

    // Refresh the deck display if the component is already initialized
    setPlayerDeck(getDeck());
    setPendingGiftToken(null);
  };

  const handleCloseGiftClaimModal = () => {
    setGiftClaimModalOpen(false);
    setClaimedGiftCard(null);
  };

  const handleDeckOpened = () => {
    setPlayerDeck(getDeck());
    setCollectionRefreshTick((tick) => tick + 1);
  };

  const handleOpenRelatedDecks = (variant) => {
    setDeckVaultFilterVariant(variant || null);
    setDeckVaultModalOpen(true);
  };

  // load puzzle
  useEffect(() => {

    // current number is based on UTC time, days since 2025-02-25 03:33 UTC
    const puzzleNumber = Math.floor((Date.now() - Date.UTC(2025, 1, 25, 3, 33)) / (1000 * 60 * 60 * 24));

    const loadPuzzleData = async () => {
      let targetPuzzleFile = 'puzzle.json'; // Default fallback
      const timestamp = Math.floor(Date.now() / 1000);

      try {
        // 1. Fetch the daily puzzle candidate
        const response = await fetch(`${puzzleNumber}.json?t=${timestamp}`);

        // 2. Explicitly check if the server returned a 200 OK status
        if (response.ok) {
          const json = await response.json();
          console.log('Checking puzzle existence:', json);

          // 3. Ensure the JSON structure is valid for your application
          if (json && json.signature) {
            targetPuzzleFile = `${puzzleNumber}.json`;
          }
        }
      } catch (error) {
        console.error('Error checking daily puzzle existence, falling back to puzzle.json:', error);
      }

      console.log('Loading puzzle from:', targetPuzzleFile);

      // 4. Fetch the determined target puzzle file
      try {
        const res = await fetch(`${targetPuzzleFile}?t=${timestamp}`);
        const data = await res.json();

        setPuzzle({ ...data });
        console.log('Loaded puzzle:', data);

        const traySlotsRaw = Array.from({ length: data.choices.length }, (_, i) => `grid_${25 + i}`);
        setTraySlots(traySlotsRaw);
        setConcernedHands([...data.concernedHands.map((hand) => ({
          ...hand,
          found: false,
        }))]);

        let tempPlacedCards = [];
        const isSamePuzzle = localStorage.getItem(KEYS.PUZZLE_SIGNATURE) === data.signature;

        // manage inter-session clock
        if (isSamePuzzle) {
          // ...
        } else {
          localStorage.setItem(KEYS.TIME, 0);
          localStorage.setItem(KEYS.HAS_INTERACTED, false);
          localStorage.setItem(KEYS.HAS_OPENED_TODAYS_BOOSTER_PACK, JSON.stringify(false));
          localStorage.removeItem(KEYS.SECRET_DECKS_OPENED);
        }

        setHasOpenedTodaysBoosterPack(JSON.parse(localStorage.getItem(KEYS.HAS_OPENED_TODAYS_BOOSTER_PACK) || 'false'));

        if (localStorage.getItem(KEYS.IS_HARD_MODE) !== null) {
          setIsHardMode(JSON.parse(localStorage.getItem(KEYS.IS_HARD_MODE)));
        }

        if (localStorage.getItem(KEYS.IS_DARK_MODE) !== null) {
          setIsDarkMode(JSON.parse(localStorage.getItem(KEYS.IS_DARK_MODE)));
        }

        if (localStorage.getItem(KEYS.SHOW_VARIANT_CARDS) !== null) {
          setShowVariantCards(JSON.parse(localStorage.getItem(KEYS.SHOW_VARIANT_CARDS)));
        }

        if (localStorage.getItem(KEYS.USE_CLASSICAL_CARDS) !== null) {
          setUseClassicalCards(JSON.parse(localStorage.getItem(KEYS.USE_CLASSICAL_CARDS)));
        }

        // load player's unlocked cards
        setPlayerDeck(getDeck());
        if (isSamePuzzle &&
          localStorage.getItem(KEYS.PLACED_CARDS) && localStorage.getItem(KEYS.PLACED_CARDS) !== 'null') {
          tempPlacedCards = JSON.parse(localStorage.getItem(KEYS.PLACED_CARDS));
          setPlacedCards(tempPlacedCards);
          setHasInteracted(JSON.parse(localStorage.getItem(KEYS.HAS_INTERACTED)));
        } else {
          localStorage.setItem(KEYS.PLACED_CARDS, []);
        }

        setPlayCards(data.choices.map((card, i) => ({
          card: card,
          id: `${card}_${i}`,
          parent: tempPlacedCards.some((placedCard) => placedCard.card === card) ?
            `grid_${tempPlacedCards.find((placedCard) => placedCard.card === card).index}`
            : traySlotsRaw[i],
        })));

        // always update the signature
        localStorage.setItem(KEYS.PUZZLE_SIGNATURE, data.signature);
        setInit(true);

      } catch (err) {
        console.error('Fatal error loading layout schema:', err);
      }
    };

    loadPuzzleData();

  }, []); // eslint-disable-line

  // evaluate placed cards winningness & other things
  // this is our general busy routine
  useEffect(() => {
    const declareVictory = () => {
      setIsWinner(true);

      if (gameStarted && hasInteracted) {
        playTadaSound();
      }

      // --- Card reward system ---
      let winningCards = [];
      concernedHands.forEach(hand => {
        winningCards = winningCards.concat(hand.hand);
      });

      if (localStorage.getItem(KEYS.PUZZLE_LASTWON) !== puzzle.signature) {

        localStorage.setItem(KEYS.PUZZLE_LASTWON, puzzle.signature);

        let baseCard = winningCards[Math.floor(Math.random() * winningCards.length)] || 'or'; // fallback to joker if something has gone wrong
        
        // Solver will convert aces to '1' if part of low straights, so we need to convert them back to 'A' for our purposes
        if (baseCard.length === 2 && baseCard.startsWith('1')) {
          baseCard = 'A' + baseCard.slice(1);
        }
        
        let card = randomizeCardVariant(baseCard, puzzle.name);

        setEarnedCard(card);
        const deck = getDeck();
        deck[card] = (deck[card] || 0) + 1;
        localStorage.setItem(KEYS.DECK, JSON.stringify(deck));
        localStorage.setItem(KEYS.LASTWON_CARD, card);

      } else {
        setEarnedCard(localStorage.getItem(KEYS.LASTWON_CARD));
      }
      // --- End card reward system ---

      clearInterval(intervalRef.current);
      const elapsed = ~~(localStorage.getItem(KEYS.TIME) || 0);
      setHrs(~~(elapsed / 3600));
      setMins(~~((elapsed % 3600) / 60));
      setSecs(~~elapsed % 60);
      setTimeout(() => { setVictoryModalOpen(true) }, 250);
    };

    const updateLocalStorage = () => {
      if (placedCards.length > 0) {
        localStorage.setItem(KEYS.PLACED_CARDS, JSON.stringify(placedCards));
        localStorage.setItem(KEYS.HAS_INTERACTED, JSON.stringify(hasInteracted));
      }
    };

    const evaluateHands = (evalSquare) => {
      return splitIntoTenHands(evalSquare)
        .map((hand, i) => ({ index: i, cards: hand }))
        .filter((hand) => hand.cards.some((card) => puzzle.choices.includes(card)))
        .filter((hand) => !hand.cards.some((card) => card === '??'))
        .map((hand) => ({ solved: Hand.solve(hand.cards, 'joker'), index: hand.index }))
        .filter((x) => !(['High Card', 'Pair'].includes(x.solved.name)));
    };

    const updateConcernedHands = (evalCheckHands) => {
      const tempConcernedHands = structuredClone(concernedHands).map((hand) => ({ ...hand, found: false }));
      evalCheckHands.forEach((evalHand) => {
        const matchedConcernedHand = tempConcernedHands.find((hand) => (!hand.found && (hand.name === evalHand.solved.name || hand.name === evalHand.solved.descr)));
        if (matchedConcernedHand) {
          matchedConcernedHand.found = true;
          matchedConcernedHand.index = evalHand.index;
          matchedConcernedHand.hand = evalHand.solved.cards.map((card) => `${card.value}${card.suit}`);
        }
      });

      setConcernedHands(tempConcernedHands);

      return tempConcernedHands;
    };

    if (!init) return;

    updateLocalStorage();

    const evalSquare = puzzle.square.slice();
    placedCards.forEach((pc) => {
      evalSquare[pc.index] = pc.card;
    });

    const evalCheckHands = evaluateHands(evalSquare);
    const tempConcernedHands = updateConcernedHands(evalCheckHands);

    if (!tempConcernedHands.some((concernedHand) => !concernedHand.found)) {
      declareVictory();
    }
  }, [init, placedCards, puzzle]); // eslint-disable-line

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (over === null) return;

    const activeCard = { ...playCards.find((card) => card.id === active.id) };
    const currentOccupier = playCards.find((c) => c.parent === over.id);

    let newPlayCards = [...playCards];
    let newPlacedCards = [...placedCards];

    if (currentOccupier !== undefined) {
      newPlayCards = [
        ...newPlayCards.filter((card) => card.id !== currentOccupier.id),
        { ...currentOccupier, parent: activeCard.parent },
      ];

      const prevIndex = ~~activeCard.parent.split('_').pop();
      newPlacedCards = [
        ...newPlacedCards.filter((pc) => pc.card !== currentOccupier.card && pc.index !== prevIndex),
        { index: prevIndex, card: currentOccupier.card },
      ];
    }

    newPlayCards = [
      ...newPlayCards.filter((card) => card.id !== active.id),
      { ...activeCard, parent: over.id },
    ];

    const index = ~~over.id.split('_').pop();
    newPlacedCards = [
      ...newPlacedCards.filter((pc) => pc.card !== activeCard.card && pc.index !== index),
      { index, card: activeCard.card },
    ];

    setPlacedCards(newPlacedCards);
    setHasInteracted(true);
    setPlayCards(newPlayCards);
    playClickSound();
  };

  const indexInConcerned = (i) => {
    const handIndexA = ~~(i / 5);
    const handIndexB = 5 + ~~(i % 5);
    return concernedHands.some((hand) => [handIndexA, handIndexB].includes(hand.index));
  };

  const renderPlayCard = (playCard) => {
    const displayCard = showVariantCards ? getDisplayCard(playCard.card, playerDeck) : playCard.card;
    const variant = showVariantCards ? getCardVariant(displayCard) : null;
    const baseCard = getBaseCard(displayCard);

    return (
      <Draggable key={playCard.id} id={playCard.id}>
        <div className='card choice'>
          {variant ? (
            <CardVariantOverlay
              variant={variant}
              imageSrc={`./assets/${cardImageFolder}/${baseCard.toLowerCase()}.png`}
              alt={playCard.card}
              title={humanCardName(playCard.card)}
              className='front'
              animated={true}
            />
          ) : (
            <img alt={playCard.card} className='front' src={`./assets/${cardImageFolder}/${displayCard.toLowerCase()}.png`} />
          )}
        </div>
      </Draggable>
    );
  };

  const renderGridCard = (card) => {
    const isHidden = puzzle.donkeyCards && !isWinner && puzzle.donkeyCards.includes(card);
    const displayCard = isHidden ? 'back1' : (showVariantCards ? getDisplayCard(card, playerDeck) : card);
    const variant = showVariantCards ? getCardVariant(displayCard) : null;
    const baseCard = getBaseCard(displayCard);

    if (variant && !isHidden) {
      return (
        <CardVariantOverlay
          variant={variant}
          imageSrc={`./assets/${cardImageFolder}/${baseCard.toLowerCase()}.png`}
          alt={card}
          title={humanCardName(card)}
          className='front'
          animated={true}
        />
      );
    } else {
      return (
        <img
          alt={card}
          className='front'
          src={`./assets/${cardImageFolder}/${displayCard.toLowerCase()}.png`}
        />
      );
    }
  };

  return (
    <div className={`App ${isDarkMode ? 'dark' : 'light'} ${getSeasonalThemeClass()} ${isWinner ? 'winner' : 'playing'}`}>

      <SharedCardVariantDefs />

      <div className={`pyro ${isWinner ? 'active' : 'inactive'}`}>
        <div className="before"></div>
        <div className="after"></div>
      </div>

      <h1 className='title'><img src="/assets/simoncards/logo_lt.png" alt="Poqueta!" /></h1>

      <nav>
        <button onClick={() => setSettingsModalOpen(true)}>Settings</button>
        <button onClick={() => setSettingsModalOpen(true)} style={{ marginRight: 'auto' }}>
          {isHardMode ? 'Hard' : 'Easy'} Mode
        </button>
        <button onClick={() => setHelpModalOpen(true)}>How to Play</button>
        {isWinner && <button onClick={() => setVictoryModalOpen(true)}>Stats</button>}
      </nav>

      {giftCardImportMessage && (
        <div className={`gift-card-notification ${giftCardImportMessage.type}`}>
          <p>{giftCardImportMessage.text}</p>
        </div>
      )}

      <p>
        Place cards to complete the hands below <span style={{ display: 'inline-block' }}>vertically or horizontally.</span>
      </p>

      <div className='hints'>
        {init && concernedHands.map((hand, i) => (
          <div key={`hint_${i}`} className={`hint ${hand.found && !isHardMode ? 'found' : 'not-found'}`}>
            {hand.name}
          </div>
        ))}
      </div>

      <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
        <div className='grid'>
          {puzzle.square.map((card, i) => {
            const playCard = playCards.find((c) => c.parent === `grid_${i}`);
            return init && card === '??' ? (
              <Droppable key={`${card}_${i}`} id={`grid_${i}`}
                className={`${isWinner && indexInConcerned(i) ? 'concern' : ''}`}>
                {playCard && renderPlayCard(playCard)}
              </Droppable>
            ) : (
              <div key={`${card}_${i}`} className={`card static ${indexInConcerned(i) ? 'concern' : ''}`}>
                {renderGridCard(card)}
              </div>
            );
          })}
        </div>

        <div className='tray-choices' style={{ '--number-of-cards': traySlots.length }}>
          {traySlots.map((id) => {
            const playCard = playCards.find((c) => c.parent === id);
            return (
              <Droppable key={id} id={id}>
                {playCard ? renderPlayCard(playCard) : ''}
              </Droppable>
            );
          })}
        </div>

        <p><small>Drag &amp; drop the cards!</small></p>
      </DndContext>

      <SettingsModal
        isOpen={settingsModalOpen}
        onClose={() => setSettingsModalOpen(false)}
        isHardMode={isHardMode}
        onHardModeChange={setIsHardMode}
        isDarkMode={isDarkMode}
        onDarkModeChange={setIsDarkMode}
        showVariantCards={showVariantCards}
        onShowVariantCardsChange={setShowVariantCards}
        useClassicalCards={useClassicalCards}
        onUseClassicalCardsChange={setUseClassicalCards}
        isWinner={isWinner}
        hasInteracted={hasInteracted}
      />

      <HelpModal
        isOpen={helpModalOpen}
        onClose={() => setHelpModalOpen(false)}
        cardImageFolder={cardImageFolder}
      />

      <VictoryModal
        isOpen={victoryModalOpen}
        onClose={() => setVictoryModalOpen(false)}
        hrs={hrs}
        mins={mins}
        secs={secs}
        earnedCard={earnedCard}
        hasOpenedTodaysBoosterPack={hasOpenedTodaysBoosterPack}
        isCopied={isCopied}
        onCopy={handleCopy}
        onShare={handleShare}
        onBoosterPackOpened={handleBoosterPackOpened}
        cardImageFolder={cardImageFolder}
        onOpenCollection={() => { setVictoryModalOpen(false); setWinningsModalOpen(true); }}
      />

      {winningsModalOpen && (
        <CollectionModal
          isOpen={winningsModalOpen}
          onClose={() => setWinningsModalOpen(false)}
          onReturnToStats={() => { setWinningsModalOpen(false); setVictoryModalOpen(true); }}
          onOpenRelatedDecks={handleOpenRelatedDecks}
          refreshTick={collectionRefreshTick}
          showVariantCards={showVariantCards}
          onShowVariantCardsChange={setShowVariantCards}
          cardImageFolder={cardImageFolder}
          giftsEnabled={giftsEnabled}
        />
      )}

      <GiftClaimModal
        isOpen={giftClaimModalOpen}
        card={claimedGiftCard}
        cardImageFolder={cardImageFolder}
        onClose={handleCloseGiftClaimModal}
      />

      <SecretDeckVaultModal
        isOpen={deckVaultModalOpen}
        onClose={() => {
          setDeckVaultModalOpen(false);
        }}
        onDeckOpened={handleDeckOpened}
        filterVariant={deckVaultFilterVariant}
        cardImageFolder={cardImageFolder}
      />

      <div className={`splash ${init && gameStarted ? 'inactive' : 'active'}`}>

        <div className="snowflakes" aria-hidden="true">
          {deck.map((card) => (
            <div key={`snow_${card}`} className="snowflake">
              <div className="inner"><img alt='' src={`./assets/${cardImageFolder}/${(showVariantCards ? getDisplayCard(card, playerDeck) : card)}.png`} /></div>
            </div>
          ))}
        </div>

        <h2 className='title'><img src="/assets/simoncards/logo_lt.png" alt="Poqueta!" /></h2>

        <p>
          Place cards to complete the required hands <span style={{ display: 'inline-block' }}>vertically or horizontally.</span>
        </p>

        {puzzle.name && <p><small>Day {puzzle.name}</small></p>}

        {puzzle.news?.text && puzzle.news?.label && (
          <div className='splash-news'>
            <div className='splash-news-label'>{puzzle.news.label}</div>
            <div className='splash-news-text'>{puzzle.news.text}</div>
          </div>
        )}

        <div className='modal-buttons'>
          {pendingGiftToken ? (
            <button onClick={handleClaimGift}>Claim Gift</button>
          ) : (
            <button onClick={() => {
              unlock();
              setGameStarted(true);
            }}>
              {isWinner ? 'Admire Puzzle' : 'Begin Puzzle'}
            </button>
          )}
        </div>

      </div>

    </div>
  );
}

export default App;

